import { styled } from 'styled-components'

import { formatColumnHeader } from 'core/components/lib/Table/formatColumnHeader'
import { TableColumnDef } from 'core/components/lib/Table/types'
import { DATE_FORMAT, formatCurrency, formatDatetime, R } from 'core/helpers'
import { StaticFieldSchema } from 'core/utils/generated/formSchemas'

export type JsonRecord = { [key: string]: unknown }

const PRIMITIVE_TYPES = new Set(['string', 'number', 'boolean', 'enum'])
const NAME_KEYS = new Set([
  'name',
  'nickname',
  'title',
  'displayName',
  'label',
  'displayId',
])
/** The record's own type name, identical on every row of a list. */
const OMITTED_KEYS = new Set(['object'])
const STATE_KEYS = new Set(['status', 'state', 'type'])
/** Platform bookkeeping: on every record, rarely what anyone scans for. */
const AUDIT_KEYS = new Set(['createdBy', 'updatedBy', 'deletedAt', 'companyId'])

const isTimestampKey = (key: string) => key.endsWith('At')
const isStateKey = (key: string) => STATE_KEYS.has(key) || key.endsWith('Type')
const isRelationKey = (key: string) => key !== 'id' && key.endsWith('Id')

const INTEGER_FORMATTER = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
})

// Adds thousands separators without rounding to a fixed decimal count, so
// schema precision (e.g. a 4-decimal-place interest rate) isn't truncated.
// Capped at 6 decimals only to guard against floating-point noise on a
// computed value (e.g. 0.30000000000000004).
const FLOAT_FORMATTER = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 6,
})

const formatNumberValue = (
  value: number,
  numberFormat: StaticFieldSchema['numberFormat'],
): string =>
  numberFormat === 'currency'
    ? formatCurrency(value) ?? String(value)
    : numberFormat === 'integer'
    ? INTEGER_FORMATTER.format(value)
    : FLOAT_FORMATTER.format(value)

/**
 * Rank order for the list view. The first tiers are named because they are
 * universal; the rest lean on schema signals, which hold across every resource
 * without a hand-maintained list. Audit is tested before the timestamp and
 * relation rules so `deletedAt` and `companyId` sort last rather than with
 * their suffix group.
 */
const priority = (field: StaticFieldSchema): number => {
  const { key } = field

  return key === 'id'
    ? 0
    : NAME_KEYS.has(key)
    ? 1
    : isStateKey(key)
    ? 2
    : AUDIT_KEYS.has(key)
    ? 8
    : field.numberFormat === 'currency'
    ? 3
    : isTimestampKey(key)
    ? 7
    : isRelationKey(key)
    ? 6
    : field.required
    ? 4
    : 5
}

export const formatCellValue = (
  field: StaticFieldSchema,
  value: unknown,
): string =>
  value == null
    ? '—'
    : field.type === 'boolean' && value === true
    ? '✓'
    : field.type === 'boolean'
    ? '–'
    : typeof value === 'string' && isTimestampKey(field.key)
    ? formatDatetime(value, DATE_FORMAT)
    : field.type === 'number' && typeof value === 'number'
    ? formatNumberValue(value, field.numberFormat)
    : String(value)

// Cells never wrap, so one long value — a public key, a stringified blob —
// would otherwise set its column's width for every row.
const MAX_CELL_CHARS = 80

export const truncateCellText = (text: string): string =>
  text.length > MAX_CELL_CHARS ? `${text.slice(0, MAX_CELL_CHARS - 1)}…` : text

/** Truncated text keeps the full value in `title`, so nothing is unreachable. */
const CellText = ({
  text,
  variant,
}: {
  text: string
  variant?: 'mono' | 'numeric'
}) => {
  const display = truncateCellText(text)
  const title = display === text ? undefined : text

  return variant === 'mono' ? (
    <Mono title={title}>{display}</Mono>
  ) : variant === 'numeric' ? (
    <NumericValue title={title}>{display}</NumericValue>
  ) : title == null ? (
    <>{display}</>
  ) : (
    <span title={title}>{display}</span>
  )
}

const deriveColumns = (
  schema: Array<StaticFieldSchema>,
): Array<TableColumnDef<JsonRecord>> =>
  R.pipe(
    schema,
    R.filter((f) => PRIMITIVE_TYPES.has(f.type) && !OMITTED_KEYS.has(f.key)),
    R.sortBy(
      (f) => priority(f),
      (f) => f.key,
    ),
    R.map(
      (field): TableColumnDef<JsonRecord> => ({
        accessorKey: field.key,
        cell: (info) => (
          <CellText
            text={formatCellValue(field, info.getValue())}
            variant={
              field.key === 'id'
                ? 'mono'
                : field.type === 'number'
                ? 'numeric'
                : undefined
            }
          />
        ),
        header: formatColumnHeader(field.key),
        id: field.key,
      }),
    ),
  )

export default deriveColumns

const Mono = styled.span`
  font-family: monospace;
`

const NumericValue = styled.span`
  font-variant-numeric: tabular-nums;
`
